import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
// @ts-ignore - IafWorkflowSvc may not be exported yet in all platform-api versions
import { IafWorkflowSvc } from '@dtplatform/platform-api';
import {
  TASK_TEMPLATES,
  INTEGRATION_PATTERNS,
  CRON_PRESETS,
  parseCronExpression,
  STATUS_COLORS,
  getEmptyWorkflowForm,
  getEmptyTaskForm,
  getEmptyScheduleForm,
} from './constants';
import type {
  WorkflowDef,
  WorkflowRun,
  WorkflowSchedule,
  WorkflowDefFormData,
  TaskDefFormData,
  ScheduleFormData,
  TaskTemplate,
  Section,
  ViewMode,
} from './types';
import './WorkflowManagementPage.css';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function WorkflowManagementPage() {
  const { createCtx, accessToken } = useAuth();
  const { selectedProject } = useApp();

  // State
  const [activeSection, setActiveSection] = useState<Section>('workflows');
  const [workflows, setWorkflows] = useState<WorkflowDef[]>([]);
  const [workflowRuns, setWorkflowRuns] = useState<WorkflowRun[]>([]);
  const [schedules, setSchedules] = useState<WorkflowSchedule[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowDef | null>(null);
  const [selectedRun, setSelectedRun] = useState<WorkflowRun | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [workflowForm, setWorkflowForm] = useState<WorkflowDefFormData>(getEmptyWorkflowForm());
  const [taskForm, setTaskForm] = useState<TaskDefFormData>(getEmptyTaskForm());
  const [scheduleForm, setScheduleForm] = useState<ScheduleFormData>(getEmptyScheduleForm());
  const [runInputParams, setRunInputParams] = useState<string>('{}');

  // Selected pattern for documentation
  const [selectedPattern, setSelectedPattern] = useState<string | null>(null);

// Get project namespace
    const getProjectNamespace = useCallback((): string[] => {
        const namespaces = selectedProject?._namespaces || [];
        return namespaces.length > 0 ? [namespaces[0]] : [];
    }, [selectedProject]);

    const getCtx = useCallback(() => {
        const namespaces = getProjectNamespace();
        return {
            authToken: accessToken || '',
            _namespaces: namespaces,
            project: selectedProject,
        };
    }, [accessToken, getProjectNamespace, selectedProject]);

  // ============================================================================
  // API CALLS - WORKFLOW DEFINITIONS
  // ============================================================================

  const fetchWorkflows = useCallback(async () => {
    if (!accessToken || !selectedProject) return;

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      const response = await IafWorkflowSvc.getWorkflowDefs({ count: 100 }, ctx);
      console.log('Workflows response:', response);
      setWorkflows(response._list || []);
    } catch (err) {
      console.error('Error fetching workflows:', err);
      setError(`Failed to fetch workflows: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedProject, getCtx]);

  const createWorkflow = async () => {
    if (!workflowForm.name) {
      setError('Workflow name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      const namespaces = getProjectNamespace();

      let inputParams = {};
      try {
        inputParams = JSON.parse(workflowForm.inputParams);
      } catch (e) {
        setError('Invalid JSON in input parameters');
        setIsLoading(false);
        return;
      }

      const workflowDef: Partial<WorkflowDef> = {
        _name: workflowForm.name,
        _description: workflowForm.description,
        _namespaces: namespaces,
        _userType: workflowForm.userType,
        _timeoutPolicy: workflowForm.timeoutPolicy,
        _timeoutSeconds: workflowForm.timeoutSeconds,
        _inputParams: inputParams,
        _steps: [],
      };

      console.log('Creating workflow:', workflowDef);
      await IafWorkflowSvc.createWorkflowDef(workflowDef, ctx);

      setSuccessMessage(`Workflow "${workflowForm.name}" created successfully!`);
      setViewMode('list');
      setWorkflowForm(getEmptyWorkflowForm());
      await fetchWorkflows();
    } catch (err) {
      console.error('Error creating workflow:', err);
      setError(`Failed to create workflow: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteWorkflow = async (workflow: WorkflowDef) => {
    if (!workflow._id || !confirm(`Delete workflow "${workflow._name}"?`)) return;

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      await IafWorkflowSvc.deleteWorkflowDef(workflow._id, ctx);
      setSuccessMessage(`Workflow "${workflow._name}" deleted!`);
      await fetchWorkflows();
    } catch (err) {
      console.error('Error deleting workflow:', err);
      setError(`Failed to delete workflow: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const runWorkflow = async (workflow: WorkflowDef) => {
    if (!workflow._id) return;

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();

      let inputParams = {};
      try {
        inputParams = JSON.parse(runInputParams);
      } catch (e) {
        inputParams = {};
      }

      const runPayload = {
        _workflowDefId: workflow._id,
        ...inputParams,
      };

      console.log('Running workflow:', runPayload);
      const result = await IafWorkflowSvc.runWorkflow(workflow._id, runPayload, ctx);

      console.log('Workflow run result:', result);
      setSuccessMessage(`Workflow "${workflow._name}" started successfully!`);

      // Fetch updated runs
      await fetchWorkflowRuns(workflow);
    } catch (err) {
      console.error('Error running workflow:', err);
      setError(`Failed to run workflow: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // API CALLS - WORKFLOW RUNS
  // ============================================================================

  const fetchWorkflowRuns = useCallback(async (workflow?: WorkflowDef) => {
    if (!accessToken || !selectedProject) return;

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      // Note: API might need to be adjusted based on actual implementation
      // Using getWorkflowStatus for now as a placeholder
      const runs: WorkflowRun[] = [];

      // If we have a specific workflow, try to get its runs
      if (workflow?._id) {
        try {
          const status = await IafWorkflowSvc.getWorkflowStatus(workflow._id, ctx);
          if (status) {
            runs.push(status as WorkflowRun);
          }
        } catch (e) {
          console.warn('Could not fetch workflow status:', e);
        }
      }

      setWorkflowRuns(runs);
    } catch (err) {
      console.error('Error fetching workflow runs:', err);
      setError(`Failed to fetch workflow runs: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedProject, getCtx]);

  const viewRunDetails = async (run: WorkflowRun) => {
    if (!run._id && !run.workflowId) return;

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      const workflowId = run._id || run.workflowId;
      const status = await IafWorkflowSvc.getWorkflowStatus(workflowId!, ctx);

      setSelectedRun(status as WorkflowRun);
      setViewMode('view');
    } catch (err) {
      console.error('Error fetching run details:', err);
      setError(`Failed to fetch run details: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // API CALLS - SCHEDULES
  // ============================================================================

  const fetchSchedules = useCallback(async (workflow?: WorkflowDef) => {
    if (!accessToken || !selectedProject || !workflow?._id) return;

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      const response = await IafWorkflowSvc.getWorkflowSchedules(workflow._id, ctx);
      console.log('Schedules response:', response);
      setSchedules(response || []);
    } catch (err) {
      console.error('Error fetching schedules:', err);
      setError(`Failed to fetch schedules: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedProject, getCtx]);

  const createSchedule = async (workflow: WorkflowDef) => {
    if (!workflow._id || !scheduleForm.cronExpression) {
      setError('Workflow and cron expression are required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();

      let inputParams = {};
      try {
        inputParams = JSON.parse(scheduleForm.inputParams);
      } catch (e) {
        setError('Invalid JSON in input parameters');
        setIsLoading(false);
        return;
      }

      const schedule: Partial<WorkflowSchedule> = {
        _name: scheduleForm.name || `Schedule for ${workflow._name}`,
        _workflowDefId: workflow._id,
        _cronExpression: scheduleForm.cronExpression,
        _enabled: scheduleForm.enabled,
        _inputParams: inputParams,
      };

      console.log('Creating schedule:', schedule);
      await IafWorkflowSvc.createWorkflowSchedule(workflow._id, schedule, ctx);

      setSuccessMessage('Schedule created successfully!');
      setViewMode('list');
      setScheduleForm(getEmptyScheduleForm());
      await fetchSchedules(workflow);
    } catch (err) {
      console.error('Error creating schedule:', err);
      setError(`Failed to create schedule: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSchedule = async (workflow: WorkflowDef, scheduleId: string) => {
    if (!workflow._id || !confirm('Delete this schedule?')) return;

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      await IafWorkflowSvc.deleteWorkflowScheduleJob(workflow._id, scheduleId, ctx);
      setSuccessMessage('Schedule deleted!');
      await fetchSchedules(workflow);
    } catch (err) {
      console.error('Error deleting schedule:', err);
      setError(`Failed to delete schedule: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    if (activeSection === 'workflows') {
      fetchWorkflows();
    }
  }, [activeSection, fetchWorkflows]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderTabBar = () => (
    <div className="workflow-tabs">
      <button
        className={`workflow-tab ${activeSection === 'workflows' ? 'active' : ''}`}
        onClick={() => setActiveSection('workflows')}
      >
        <i className="fas fa-project-diagram"></i>
        Workflow Definitions
      </button>
      <button
        className={`workflow-tab ${activeSection === 'tasks' ? 'active' : ''}`}
        onClick={() => setActiveSection('tasks')}
      >
        <i className="fas fa-tasks"></i>
        Task Templates
      </button>
      <button
        className={`workflow-tab ${activeSection === 'runs' ? 'active' : ''}`}
        onClick={() => setActiveSection('runs')}
      >
        <i className="fas fa-play-circle"></i>
        Workflow Runs
      </button>
      <button
        className={`workflow-tab ${activeSection === 'schedules' ? 'active' : ''}`}
        onClick={() => setActiveSection('schedules')}
      >
        <i className="fas fa-calendar-alt"></i>
        Schedules
      </button>
      <button
        className={`workflow-tab ${activeSection === 'docs' ? 'active' : ''}`}
        onClick={() => setActiveSection('docs')}
      >
        <i className="fas fa-book"></i>
        Integration Patterns
      </button>
    </div>
  );

  const renderWorkflowsSection = () => (
    <div className="section-content">
      {viewMode === 'list' ? (
        <>
          <div className="section-header">
            <h2>
              <i className="fas fa-project-diagram"></i>
              Workflow Definitions ({workflows.length})
            </h2>
            <button className="btn-primary" onClick={() => setViewMode('create')}>
              <i className="fas fa-plus"></i>
              Create Workflow
            </button>
          </div>

          {isLoading && workflows.length === 0 ? (
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading workflows...</span>
            </div>
          ) : workflows.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-inbox"></i>
              <p>No workflow definitions found</p>
              <p className="hint">Create your first workflow to get started</p>
            </div>
          ) : (
            <div className="workflows-grid">
              {workflows.map((workflow) => (
                <div key={workflow._id} className="workflow-card">
                  <div className="workflow-card-header">
                    <h3>{workflow._name}</h3>
                    <div className="workflow-card-actions">
                      <button
                        className="btn-icon"
                        onClick={() => {
                          setSelectedWorkflow(workflow);
                          fetchWorkflowRuns(workflow);
                        }}
                        title="View runs"
                      >
                        <i className="fas fa-list"></i>
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => {
                          setSelectedWorkflow(workflow);
                          fetchSchedules(workflow);
                          setActiveSection('schedules');
                        }}
                        title="Manage schedules"
                      >
                        <i className="fas fa-calendar"></i>
                      </button>
                      <button
                        className="btn-icon danger"
                        onClick={() => deleteWorkflow(workflow)}
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>

                  {workflow._description && (
                    <p className="workflow-card-desc">{workflow._description}</p>
                  )}

                  <div className="workflow-card-meta">
                    <span className="meta-item">
                      <i className="fas fa-code-branch"></i>
                      {workflow._steps?.length || 0} steps
                    </span>
                    <span className="meta-item">
                      <code>{workflow._userType}</code>
                    </span>
                  </div>

                  <div className="workflow-card-footer">
                    <button
                      className="btn-primary small"
                      onClick={() => {
                        setSelectedWorkflow(workflow);
                        setRunInputParams('{}');
                        const modal = document.getElementById('run-workflow-modal');
                        if (modal) modal.style.display = 'flex';
                      }}
                    >
                      <i className="fas fa-play"></i>
                      Run Workflow
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="form-container">
          <div className="form-header">
            <h2>
              <i className="fas fa-plus-circle"></i>
              Create Workflow Definition
            </h2>
            <button className="btn-icon" onClick={() => setViewMode('list')}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="form-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Name*</label>
                <input
                  type="text"
                  value={workflowForm.name}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, name: e.target.value })}
                  placeholder="e.g., data_processing_workflow"
                />
              </div>

              <div className="form-group">
                <label>User Type</label>
                <input
                  type="text"
                  value={workflowForm.userType}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, userType: e.target.value })}
                  placeholder="e.g., workflow_def"
                />
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={workflowForm.description}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, description: e.target.value })}
                  placeholder="Describe what this workflow does..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Timeout Policy</label>
                <select
                  value={workflowForm.timeoutPolicy}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, timeoutPolicy: e.target.value as any })}
                >
                  <option value="TIME_OUT_WF">Time Out Workflow</option>
                  <option value="ALERT_ONLY">Alert Only</option>
                </select>
              </div>

              <div className="form-group">
                <label>Timeout (seconds)</label>
                <input
                  type="number"
                  min={0}
                  value={workflowForm.timeoutSeconds}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, timeoutSeconds: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="form-group full-width">
                <label>Input Parameters (JSON)</label>
                <textarea
                  value={workflowForm.inputParams}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, inputParams: e.target.value })}
                  placeholder='{"param1": "value1"}'
                  rows={4}
                  className="code-input"
                />
              </div>
            </div>
          </div>

          <div className="form-footer">
            <button className="btn-secondary" onClick={() => setViewMode('list')}>
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={createWorkflow}
              disabled={isLoading || !workflowForm.name}
            >
              {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-plus"></i>}
              Create Workflow
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderTasksSection = () => (
    <div className="section-content">
      <div className="section-header">
        <h2>
          <i className="fas fa-tasks"></i>
          Built-in Task Types ({TASK_TEMPLATES.length})
        </h2>
      </div>

      <div className="info-banner">
        <i className="fas fa-info-circle"></i>
        <div>
          <strong>Task Templates:</strong> These are the built-in task types you can use in your workflow definitions.
          Click on any task to see detailed documentation and examples.
        </div>
      </div>

      <div className="task-templates-grid">
        {TASK_TEMPLATES.map((template) => (
          <div
            key={template.type}
            className="task-template-card"
            style={{ borderLeftColor: template.color }}
          >
            <div className="task-template-header">
              <div className="task-template-icon" style={{ backgroundColor: `${template.color}20`, color: template.color }}>
                <i className={`fas ${template.icon}`}></i>
              </div>
              <div>
                <h3>{template.name}</h3>
                <span className="task-category">{template.category}</span>
              </div>
            </div>

            <p className="task-template-desc">{template.description}</p>

            <div className="task-template-meta">
              <code className="task-type">{template.type}</code>
            </div>

            {template.documentation && (
              <details className="task-documentation">
                <summary>
                  <i className="fas fa-book"></i>
                  Documentation & Examples
                </summary>
                <div className="documentation-content">
                  <pre>{template.documentation}</pre>
                </div>
              </details>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderRunsSection = () => (
    <div className="section-content">
      <div className="section-header">
        <h2>
          <i className="fas fa-play-circle"></i>
          Workflow Runs
          {selectedWorkflow && ` - ${selectedWorkflow._name}`}
        </h2>
        {selectedWorkflow && (
          <button className="btn-secondary" onClick={() => setSelectedWorkflow(null)}>
            <i className="fas fa-arrow-left"></i>
            Back to All
          </button>
        )}
      </div>

      {!selectedWorkflow ? (
        <div className="empty-state">
          <i className="fas fa-mouse-pointer"></i>
          <p>Select a workflow from the Workflow Definitions tab to view its runs</p>
        </div>
      ) : workflowRuns.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-inbox"></i>
          <p>No runs found for this workflow</p>
          <p className="hint">Run the workflow to see execution history</p>
        </div>
      ) : (
        <div className="runs-table">
          <table>
            <thead>
              <tr>
                <th>Run ID</th>
                <th>Status</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {workflowRuns.map((run) => (
                <tr key={run._id || run.workflowId}>
                  <td>
                    <code>{(run._id || run.workflowId || '').substring(0, 8)}</code>
                  </td>
                  <td>
                    <span
                      className="status-badge"
                      style={{ backgroundColor: STATUS_COLORS[run._status || ''] || '#6b7280' }}
                    >
                      {run._status || 'UNKNOWN'}
                    </span>
                  </td>
                  <td>{run._startTime ? new Date(run._startTime).toLocaleString() : '-'}</td>
                  <td>
                    {run._startTime && run._endTime
                      ? `${Math.round((new Date(run._endTime).getTime() - new Date(run._startTime).getTime()) / 1000)}s`
                      : run._startTime
                      ? 'Running...'
                      : '-'}
                  </td>
                  <td>
                    <button
                      className="btn-icon"
                      onClick={() => viewRunDetails(run)}
                      title="View details"
                    >
                      <i className="fas fa-eye"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedRun && viewMode === 'view' && (
        <div className="modal-overlay" onClick={() => setViewMode('list')}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-info-circle"></i>
                Run Details
              </h2>
              <button className="btn-icon" onClick={() => setViewMode('list')}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <pre className="run-result">{JSON.stringify(selectedRun, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderSchedulesSection = () => (
    <div className="section-content">
      <div className="section-header">
        <h2>
          <i className="fas fa-calendar-alt"></i>
          Workflow Schedules
          {selectedWorkflow && ` - ${selectedWorkflow._name}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {selectedWorkflow && viewMode === 'list' && (
            <button className="btn-primary" onClick={() => setViewMode('create')}>
              <i className="fas fa-plus"></i>
              Create Schedule
            </button>
          )}
          {selectedWorkflow && (
            <button className="btn-secondary" onClick={() => setSelectedWorkflow(null)}>
              <i className="fas fa-arrow-left"></i>
              Back
            </button>
          )}
        </div>
      </div>

      {!selectedWorkflow ? (
        <div className="empty-state">
          <i className="fas fa-mouse-pointer"></i>
          <p>Select a workflow to manage its schedules</p>
        </div>
      ) : viewMode === 'create' ? (
        <div className="form-container">
          <div className="form-body">
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Schedule Name</label>
                <input
                  type="text"
                  value={scheduleForm.name}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                  placeholder="e.g., Daily Processing"
                />
              </div>

              <div className="form-group full-width">
                <label>Cron Expression*</label>
                <select
                  value={scheduleForm.cronExpression}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, cronExpression: e.target.value })}
                >
                  {CRON_PRESETS.map((preset) => (
                    <option key={preset.value} value={preset.value}>
                      {preset.label} ({preset.value})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={scheduleForm.enabled}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, enabled: e.target.checked })}
                  />
                  <span>Enabled</span>
                </label>
              </div>

              <div className="form-group full-width">
                <label>Input Parameters (JSON)</label>
                <textarea
                  value={scheduleForm.inputParams}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, inputParams: e.target.value })}
                  placeholder='{"param1": "value1"}'
                  rows={4}
                  className="code-input"
                />
              </div>
            </div>
          </div>

          <div className="form-footer">
            <button className="btn-secondary" onClick={() => setViewMode('list')}>
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={() => createSchedule(selectedWorkflow)}
              disabled={isLoading}
            >
              {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-plus"></i>}
              Create Schedule
            </button>
          </div>
        </div>
      ) : schedules.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-inbox"></i>
          <p>No schedules found</p>
          <p className="hint">Create a schedule to run this workflow automatically</p>
        </div>
      ) : (
        <div className="schedules-grid">
          {schedules.map((schedule) => (
            <div key={schedule._id} className="schedule-card">
              <div className="schedule-card-header">
                <h3>{schedule._name || 'Unnamed Schedule'}</h3>
                <button
                  className="btn-icon danger"
                  onClick={() => schedule._id && deleteSchedule(selectedWorkflow, schedule._id)}
                  title="Delete"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>

              <div className="schedule-card-body">
                <div className="schedule-meta">
                  <span className="meta-item">
                    <i className="fas fa-clock"></i>
                    {parseCronExpression(schedule._cronExpression)}
                  </span>
                  <span className="meta-item">
                    <code>{schedule._cronExpression}</code>
                  </span>
                </div>

                <span className={`status-badge ${schedule._enabled ? 'enabled' : 'disabled'}`}>
                  {schedule._enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderDocsSection = () => (
    <div className="section-content">
      <div className="section-header">
        <h2>
          <i className="fas fa-book"></i>
          Integration Patterns
        </h2>
      </div>

      <div className="info-banner">
        <i className="fas fa-lightbulb"></i>
        <div>
          <strong>Integration Patterns:</strong> Learn common enterprise integration patterns and how to implement them
          using workflow tasks. These patterns help you build robust, scalable data integration solutions.
        </div>
      </div>

      <div className="patterns-grid">
        {Object.entries(INTEGRATION_PATTERNS).map(([key, pattern]) => (
          <div
            key={key}
            className="pattern-card"
            onClick={() => setSelectedPattern(selectedPattern === key ? null : key)}
          >
            <div className="pattern-header">
              <div className="pattern-icon">
                <i className={`fas ${pattern.icon}`}></i>
              </div>
              <h3>{pattern.title}</h3>
            </div>

            <p className="pattern-desc">{pattern.description}</p>

            {selectedPattern === key && (
              <div className="pattern-example">
                <pre>{pattern.example}</pre>
              </div>
            )}

            <div className="pattern-footer">
              <span className="pattern-hint">
                {selectedPattern === key ? 'Click to collapse' : 'Click for example'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  if (!selectedProject) {
    return (
      <div className="workflow-management">
        <div className="empty-state-container">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>No Project Selected</h2>
          <p>Please select a project to manage workflows.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workflow-management">
      <div className="workflow-header">
        <div className="workflow-title">
          <i className="fas fa-project-diagram"></i>
          <h1>Workflow Management</h1>
        </div>
        <p className="workflow-subtitle">
          Manage workflow definitions, tasks, schedules, and monitor executions for{' '}
          <strong>{selectedProject._name}</strong>
        </p>
      </div>

      {error && (
        <div className="workflow-message error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="workflow-message success">
          <i className="fas fa-check-circle"></i>
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {renderTabBar()}

      <div className="workflow-content">
        {activeSection === 'workflows' && renderWorkflowsSection()}
        {activeSection === 'tasks' && renderTasksSection()}
        {activeSection === 'runs' && renderRunsSection()}
        {activeSection === 'schedules' && renderSchedulesSection()}
        {activeSection === 'docs' && renderDocsSection()}
      </div>

      {/* Run Workflow Modal */}
      <div id="run-workflow-modal" className="modal-overlay" style={{ display: 'none' }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>
              <i className="fas fa-play"></i>
              Run Workflow: {selectedWorkflow?._name}
            </h2>
            <button
              className="btn-icon"
              onClick={() => {
                const modal = document.getElementById('run-workflow-modal');
                if (modal) modal.style.display = 'none';
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="modal-body">
            <div className="form-group">
              <label>Input Parameters (JSON)</label>
              <textarea
                value={runInputParams}
                onChange={(e) => setRunInputParams(e.target.value)}
                placeholder='{"param1": "value1"}'
                rows={8}
                className="code-input"
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              className="btn-secondary"
              onClick={() => {
                const modal = document.getElementById('run-workflow-modal');
                if (modal) modal.style.display = 'none';
              }}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={() => {
                if (selectedWorkflow) {
                  runWorkflow(selectedWorkflow);
                  const modal = document.getElementById('run-workflow-modal');
                  if (modal) modal.style.display = 'none';
                }
              }}
              disabled={isLoading}
            >
              {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-play"></i>}
              Run Workflow
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkflowManagementPage;
